import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, ofType, Effect } from '@ngrx/effects';
import { Observable, concat, of } from 'rxjs';
import * as ClaimCmeCeActions from './actions';
import { pluck, switchMap, map, catchError, tap, mergeMap } from 'rxjs/operators';
import { ClaimCmeCeService } from '../data/services/claim-cme-ce.service';
import { LabAffiliation } from '../data/models/lab-affiliation.model';
import { State } from './state';
import { ActivityValidation } from '../data/models/activity-validation.model';

@Injectable()
export class ClaimCmeCeEffects {
    constructor(private store: Store<State>, private actions$: Actions, private claimCmeCeService: ClaimCmeCeService) {}

    @Effect()
    getLabAffiliations$: Observable<Action> = this.actions$.pipe(
        ofType(ClaimCmeCeActions.ActionTypes.GET_LAB_AFFILIATIONS),
        switchMap(() => {
            return this.claimCmeCeService.getLabAffiliations$().pipe(
                map((affiliations: LabAffiliation[]) => new ClaimCmeCeActions.GetLabAffiliationsSuccess(affiliations)),
                catchError((err) => [new ClaimCmeCeActions.GetLabAffiliationsFailure(err)])
            );
        })
    );

    //
    // Contextual Help:
    //
    @Effect()
    getContextualHelp$: Observable<Action> = this.actions$.pipe(
        ofType(ClaimCmeCeActions.ActionTypes.GET_CONTEXTUAL_HELP),
        switchMap(() =>
            this.claimCmeCeService.getContextualHelp().pipe(
                map((contextualHelpHTML: string) => new ClaimCmeCeActions.GetContextualHelpSuccess(contextualHelpHTML)),
                catchError((err) => [new ClaimCmeCeActions.GetContextualHelpFailure(err)])
            )
        )
    );

   @Effect()
getLabInfo$: Observable<Action> = this.actions$.pipe(
    ofType(ClaimCmeCeActions.ActionTypes.GET_LAB_INFO),
    pluck('payload'),
    switchMap(({ capNumber }) => {

        return this.claimCmeCeService.getLabDetailsFromCapNumber$(capNumber).pipe(

            mergeMap((response: any) => {

                const labAffiliation: LabAffiliation = {
                    capNumber: response.capNumber,
                    orgName: response.businessName1 || response.businessName2,
                    address1: response.address?.address1,
                    city: response.address?.city,
                    state: response.address?.state,
                    postalCode: response.address?.postalCode
                } as LabAffiliation;

                return concat(
                    of(new ClaimCmeCeActions.GetLabInfoSuccess(labAffiliation)),
                    of(new ClaimCmeCeActions.OpenConfirmationWindow())
                );
            }),

            catchError((err) => [new ClaimCmeCeActions.GetLabInfoFailure(err)])
        );
    })
);

    @Effect({ dispatch: false })
    getLabInfoFailure$: Observable<any> = this.actions$.pipe(
        ofType(ClaimCmeCeActions.ActionTypes.GET_LAB_INFO_FAILURE),
        pluck('payload'),
        tap(() => {
            console.log('FAILURE');
            this.store.dispatch(new ClaimCmeCeActions.OpenConfirmationWindow());
        })
    );

    @Effect()
    assignKitToActivity$: Observable<Action> = this.actions$.pipe(
        ofType(ClaimCmeCeActions.ActionTypes.ASSIGN_KIT_TO_ACTIVITY),
        pluck('payload'),
        switchMap(({ orgNumber, kitNumber }) => {
            return this.claimCmeCeService.assignKitToActivity$(orgNumber, kitNumber).pipe(
                map(
                    (activityValidation: ActivityValidation) =>
                        new ClaimCmeCeActions.AssignKitToActivitySuccess(activityValidation)
                ),
                catchError((err) => [
                    new ClaimCmeCeActions.AssignKitToActivityFailure(err),
                    new ClaimCmeCeActions.CloseConfirmationWindow(),
                ])
            );
        })
    );

    @Effect({ dispatch: false })
    assignKitToActivitySuccess$: Observable<Action> = this.actions$.pipe(
        ofType(ClaimCmeCeActions.ActionTypes.ASSIGN_KIT_TO_ACTIVITY_SUCCESS),
        tap(() => this.store.dispatch(new ClaimCmeCeActions.CloseConfirmationWindow()))
    );

    @Effect()
    getActivityRedirectUrl$: Observable<Action> = this.actions$.pipe(
        ofType(ClaimCmeCeActions.ActionTypes.GET_ACTIVITY_REDIRECT_URL),
        pluck('payload'),
        switchMap(({ activityCode }) => {
            return this.claimCmeCeService.getActivityRedirectUrl$(activityCode).pipe(
                map((redirectUrl: string) => new ClaimCmeCeActions.GetActivityRedirectUrlSuccess(redirectUrl)),
                catchError((err) => {
                    console.error(err);
                    return [new ClaimCmeCeActions.GetActivityRedirectUrlFailure(err)];
                })
            );
        })
    );
}
